import React, { Fragment as F } from 'react'
import c from 'classnames'
import { connect } from 'react-redux'
import moment from 'moment'
import get from 'lodash/get'

import Icon from 'discada/dist/Icon'
import { moneyShort, numShort } from 'utils'
import { getEntity } from 'selectors/entitySelectors'
import { getIsLoggedIn } from 'selectors/authSelectors'

import CherryGauge from 'components/widgets/CherryGauge'
import { ReactionGauge } from 'components/Stats/ReactionStats'

import {
  obfusName,
  obfusPhone,
  obfusEmail,
  obfusDevice,
  obfusSources,
} from 'utils'

import './LeadHeader.scss'

export const STATS = {
  lastSeen: {
    label: 'Last Seen',
    value: v => moment(v).fromNow(),
    icon: 'hourglass',
  },
  illuminations: {
    label: 'Activities',
    value: (_, { lead }) => get(lead, 'summary.illuminationsCount', 0),
    icon: 'lightbulb',
  },
  averageLeasePayment: {
    label: 'Lease',
    value: (v, s) =>
      v > 0
        ? `${moneyShort(v)}/${Math.floor(get(s, 'averageLeaseTerm'))}mo`
        : 'N/A',
    icon: 'file-invoice-dollar',
  },
  averageLoanPayment: {
    label: 'Loan',
    value: (v, s) =>
      v > 0
        ? `${moneyShort(v)}/${Math.floor(get(s, 'averageLoanTerm'))}mo`
        : 'N/A',
    icon: 'file-invoice-dollar',
  },
  averageDown: {
    label: 'Down Payment',
    value: v => moneyShort(Math.floor(v)),
    icon: 'comment-dollar',
  },
  averageCreditScore: {
    label: 'Credit Score',
    value: v => Math.floor(v),
    icon: 'credit-card',
  },
  modeLeasePayment: {
    label: 'Expected Lease',
    value: (v, s) =>
      v > 0
        ? `${moneyShort(v)} for ${Math.floor(get(s, 'modeLeaseTerm'))}mo`
        : 'N/A',
    icon: 'file-invoice-dollar',
  },
  modeLoanPayment: {
    label: 'Expected Loan',
    value: (v, s) =>
      v > 0
        ? `${moneyShort(v)}  for ${Math.floor(get(s, 'modeLoanTerm'))}mo`
        : 'N/A',
    icon: 'file-invoice-dollar',
  },
  modeDown: {
    label: 'Expected Down Payment',
    value: v => moneyShort(v),
    icon: 'comment-dollar',
  },
  modeCreditScore: {
    label: 'Expected Credit',
    value: v => v,
    icon: 'credit-card',
  },
  modeMiles: {
    label: 'Expected Miles',
    value: v => v,
    icon: 'file-invoice-dollar',
  },
  zipCode: {
    label: 'Zipcode',
    value: v => v,
    icon: 'map-marker-alt',
  },
  isMobile: {
    label: 'Device',
    value: v => (v ? 'Mobile' : 'Desktop'),
    icon: 'hand-pointer',
  },
  vehicles: {
    label: 'Vehicles',
    value: (_, { lead }) => get(lead, 'summary.vehicleIds', []).length,
  },
  quotes: {
    label: 'Quotes',
    value: (_, { lead }) => get(lead, 'summary.quoteIds', []).length,
  },
  reactions: {
    label: 'Reactions',
    value: (_, { lead }) => get(lead, 'summary.reactionsCount', []).length,
  },
  camebackCount: {
    label: 'Visits',
    value: (_, { lead }) => 1 + get(lead, 'summary.cameBackCount', 0),
  },
}

export const STAT_DOTS = [
  'illuminations',
  //'vehicles',
  //'quotes',
  'camebackCount',
]

export const SUMMARY_BLOCKS = [
  'modeLeasePayment',
  'modeLoanPayment',
  'modeDown',
  'modeCreditScore',
  'modeMiles',
  'zipCode',
  'isMobile',
]

export function LeadStatDot({ label, value }) {
  return (
    <span
      className={c('lead-stats-dot', {
        bit: parseInt(value, 10) > 0,
        hooked: parseInt(value, 10) > 10,
      })}
    >
      <strong>{numShort(value)}</strong>
      <span>{label}</span>
    </span>
  )
}

export function LeadSummaryBlock({ icon, label, value }) {
  return (
    <div className="lead-summary-block">
      <Icon fixedWidth icon={icon} />
      <span>
        <strong>{value}</strong> {label}
      </span>
    </div>
  )
}

export function LeadHeader(props) {
  const {
      firstName,
      lastName,
      email,
      phone,
      summary,
      lastSeen,
      showStats,
      canDo,
      lead,
      hideVehicles,
    } = props,
    knownName = firstName || lastName,
    notKnownCustomerLabel = 'Unknown Customer',
    notKnownLabel = <em className="unknown-label">Unknown</em>,
    {
      summary: {
        sources = [],
        campaigns = [],
        vehicles = [],
        browser: {
          os: osName,
          mobile: isMobile = false,
          name: browserName,
        } = {},
      } = {},
    } = lead

  const { engagementScore } = summary

  return (
    <div className="lead-row">
      <F>
        <div className="lead-contact">
          <h1 className={c('lead-contact-name', { unknown: !knownName })}>
            {knownName
              ? `${firstName} ${obfusName(lastName, canDo)}`
              : notKnownCustomerLabel}{' '}
            <br />
          </h1>
          <div className="lead-contact-details">
            <div className="contact-information">
              <span className={c('lead-contact-datum', { unknown: !email })}>
                <Icon fixedWidth icon="envelope" />
                {obfusEmail(email, canDo) || notKnownLabel}
              </span>
              <span className={c('lead-contact-datum', { unknown: !phone })}>
                <Icon fixedWidth icon="phone" />
                {obfusPhone(phone, canDo) || notKnownLabel}
              </span>
              <span
                className={c('lead-contact-datum', {
                  unknown: sources.length === 0,
                })}
              >
                <Icon fixedWidth icon="eye" />
                Sources:{' '}
                {sources.length > 0
                  ? obfusSources(sources, canDo)
                  : notKnownLabel}
                , Campaigns :{' '}
                {campaigns.length > 0
                  ? obfusSources(campaigns, canDo)
                  : notKnownLabel}
              </span>
              <span
                className={c('lead-contact-datum', {
                  unknown: !(osName && browserName),
                })}
              >
                <Icon fixedWidth icon={isMobile ? 'mobile-alt' : 'desktop'} />
                {browserName && osName
                  ? obfusDevice(browserName, osName, canDo)
                  : notKnownLabel}
              </span>
            </div>
            {!hideVehicles && vehicles.length > 0 && (
              <div className="lead-activity">
                <ul className={c('lead-contact-datum')}>
                  {vehicles
                    .filter((_, i) => i < 3)
                    .map(v => (
                      <li key={v}>
                        <Icon fixedWidth icon="car" /> {v}
                      </li>
                    ))}
                  {vehicles.length > 3 && <li>{vehicles.length - 3} more</li>}
                </ul>
              </div>
            )}
          </div>
        </div>
        <div className="lead-dots">
          <div>
            {get(summary, 'intent.length', 0) > 0 && (
              <ReactionGauge reactions={summary.intent} />
            )}
            <CherryGauge engagementScore={engagementScore} />
          </div>
          <div>
            {STAT_DOTS.map(k => (
              <LeadStatDot
                key={k}
                {...STATS[k]}
                value={STATS[k].value(get(props, k), props)}
              />
            ))}
          </div>
          <span className={c('lead-last-seen')}>
            <Icon fixedWidth icon="clock" />
            Last Seen {moment(lastSeen).fromNow()}
          </span>
        </div>

        {showStats && (
          <div className="lead-stats">
            {SUMMARY_BLOCKS.map(k => (
              <LeadSummaryBlock
                key={k}
                {...STATS[k]}
                value={STATS[k].value(get(summary, k), summary)}
              />
            ))}
          </div>
        )}
      </F>
    </div>
  )
}

function mapStateToProps(state, op) {
  let lead = getEntity(state, op.id, {}),
    summary = get(lead, 'summary', {}),
    firstName = get(summary, 'firstName', ''),
    lastName = get(summary, 'lastName', '')

  return {
    ...op,
    lead,
    firstName,
    lastName,
    summary,
    email: get(summary, 'email'),
    phone: get(summary, 'phone'),
    lastSeen: get(summary, 'lastSeen'),
    canDo: getIsLoggedIn(state),
  }
}

export default connect(mapStateToProps)(LeadHeader)
